import React, { useState, useMemo } from "react";
import { Paper } from "@mui/material";
import { TableVirtuoso } from "react-virtuoso";
import { sortData } from "./sortUtils";
import { CustomVirtuosoTableComponents } from "./TableComponents";
import TableHeader from "./TableHeader";
import TableRowContent from "./TableRowContent";
import TableStateMessage from "./TableStateMessage";
import TableSkeleton from "./TableSkeleton";

interface ColumnDef<T> {
  key: keyof T | string;
  id: keyof T | string;
  label: string;
  width?: number;
  align?: "left" | "right" | "center";
  sortable?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  fontSize?: string;
  truncate?: number;
  wrap?: boolean;
}

interface VirtualTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  height?: number | string;
  minWidth?: number | string;
  defaultSortColumn?: keyof T | string;
  defaultSortDirection?: "asc" | "desc";
  loading?: boolean;
  error?: string | null;
}

export default function VirtualizedTable<T>({
  data,
  columns,
  height = 400,
  minWidth = "auto",
  defaultSortColumn,
  defaultSortDirection = "asc",
  loading = false,
  error = null,
}: VirtualTableProps<T>) {
  const [sortConfig, setSortConfig] = useState<{
    column: keyof T | string | null;
    direction: "asc" | "desc";
  }>({
    column: defaultSortColumn || null,
    direction: defaultSortDirection,
  });

  const sortedData = useMemo(
    () => sortData(data, sortConfig, columns),
    [data, sortConfig, columns]
  );

  const handleSort = (columnId: keyof T | string) => {
    setSortConfig((prev) => ({
      column: columnId,
      direction:
        prev.column === columnId && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const isEmpty = !loading && !error && data.length === 0;

  return (
    <Paper elevation={0} sx={{ height, width: "100%", position: "relative" }}>
      {loading && <TableSkeleton columns={columns} />}
      {error && <TableStateMessage message={error} isError />}
      {isEmpty && <TableStateMessage message="No data available" />}
      <TableVirtuoso
        data={sortedData}
        components={CustomVirtuosoTableComponents}
        fixedHeaderContent={() => (
          <TableHeader
            columns={columns}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        )}
        itemContent={(index, row) => (
          <TableRowContent row={row} columns={columns} />
        )}
      />
    </Paper>
  );
}